// ContactActions — Call / Text / Directions for the record's customer (UX-074).
//
// The crew lead standing in a driveway needs these three, big, at the top of
// the job. Call uses the business line (softphone) when it's connected;
// otherwise it falls back to the phone's own dialer via tel: (DECISIONS §15).
// Text opens the Inbox thread; Directions opens Maps.

import { Phone, MessageSquare, Navigation, Mail } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useCallStore } from '@/stores/callStore'
import { formatPhone, telHref } from '@/lib/format'
import type { Record_ } from './types'

export function mapsHref(address: string): string {
  return `https://maps.google.com/?q=${encodeURIComponent(address)}`
}

/** Inbox deep link: the contact's thread, or a new message to the number. */
export function textHref(record: Pick<Record_, 'contactId' | 'phone'>): string | null {
  if (record.contactId) return `/inbox?thread=${encodeURIComponent(record.contactId)}`
  if (record.phone) return `/inbox?compose=${encodeURIComponent(record.phone)}`
  return null
}

const BTN: React.CSSProperties = {
  flex: '1 1 0',
  minWidth: 0,
  minHeight: 48,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '0 10px',
  borderRadius: 9999,
  fontSize: 14,
  fontWeight: 600,
  color: 'rgb(var(--fg-rgb))',
  background: 'rgb(var(--fg-rgb) / 0.08)',
  border: '0.5px solid rgb(var(--fg-rgb) / 0.14)',
  textDecoration: 'none',
  cursor: 'pointer',
}

export function ContactActions({ record }: { record: Record_ }) {
  const device = useCallStore((s) => s.device)
  const phoneOffline = useCallStore((s) => s.phoneOffline)
  const initiateCall = useCallStore((s) => s.initiateCall)
  const businessLine = Boolean(device) && !phoneOffline

  const hasAddress = Boolean(record.address && record.address !== '—')
  const text = textHref(record)
  if (!record.phone && !record.email && !hasAddress) return null

  const context = record.jobId ? { jobId: record.jobId } : record.quoteId ? { quoteId: record.quoteId } : undefined

  return (
    <div data-testid="contact-actions" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {record.phone && (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--midnight-text2)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Phone style={{ width: 13, height: 13, flexShrink: 0 }} aria-hidden />
          <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flexShrink: 0 }}>{formatPhone(record.phone)}</span>
          {record.email && <span style={{ color: 'var(--midnight-text3)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {record.email}</span>}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        {record.phone && (businessLine ? (
          <button
            type="button"
            style={BTN}
            aria-label={`Call ${record.customer}`}
            onClick={() => { void initiateCall({ contactId: record.contactId, phone: record.phone!, context, name: record.customer }) }}
          >
            <Phone style={{ width: 16, height: 16 }} aria-hidden />
            Call
          </button>
        ) : (
          <a href={telHref(record.phone)} style={BTN} aria-label={`Call ${record.customer}`} data-testid="call-tel">
            <Phone style={{ width: 16, height: 16 }} aria-hidden />
            Call
          </a>
        ))}
        {record.phone && text && (
          <Link to={text} style={BTN} aria-label={`Text ${record.customer}`}>
            <MessageSquare style={{ width: 16, height: 16 }} aria-hidden />
            Text
          </Link>
        )}
        {!record.phone && record.email && (
          <a href={`mailto:${record.email}`} style={BTN} aria-label={`Email ${record.customer}`}>
            <Mail style={{ width: 16, height: 16 }} aria-hidden />
            Email
          </a>
        )}
        {hasAddress && (
          <a href={mapsHref(record.address)} target="_blank" rel="noopener noreferrer" style={BTN} aria-label={`Directions to ${record.address}`}>
            <Navigation style={{ width: 16, height: 16 }} aria-hidden />
            Directions
          </a>
        )}
      </div>
    </div>
  )
}
